import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, UserCheck, Activity } from "lucide-react";

const STAGES = ["new", "qualified", "proposal", "negotiation", "converted"];

export default function Leads() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", source: "Referral", product_interest: "", email: "", phone: "", next_follow_up: "" });
  const canEdit = user.role === "relationship_manager" || user.role === "administrator";

  const load = () => api.get("/leads").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  if (!rows) return <Spinner />;

  const create = async () => {
    try { await api.post("/leads", form); toast.success("Lead created"); setOpen(false);
      setForm({ name: "", source: "Referral", product_interest: "", email: "", phone: "", next_follow_up: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const setStage = async (id, stage) => { await api.put(`/leads/${id}`, { stage }); load(); };
  const convert = async (id) => {
    try { const r = await api.post(`/leads/${id}/convert`); toast.success(`Converted to ${r.data.customer.code}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addActivity = async (id) => {
    const note = prompt("Activity note");
    if (note) { await api.post(`/leads/${id}/activity`, { note }); toast.success("Activity logged"); load(); }
  };

  return (
    <div>
      <PageHeader overline="Pipeline" title="Leads" subtitle="Track enquiries, follow-ups and conversion to customers."
        testid="leads-header"
        actions={canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="gap-1" data-testid="new-lead-btn"><Plus className="h-4 w-4" /> New Lead</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" /></div>
                <div><Label>Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                    <SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Referral", "Event", "Digital Campaign", "Walk-in", "Website"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Product Interest</Label><Input value={form.product_interest} onChange={(e) => setForm({ ...form, product_interest: e.target.value })} data-testid="lead-product" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                </div>
                <div><Label>Next follow-up</Label><Input type="date" value={form.next_follow_up} onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="save-lead-btn">Create Lead</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      <Card className="p-4 sm:p-5">
        {rows.length === 0 ? <EmptyState title="No leads yet" /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Source</TableHead><TableHead>Interest</TableHead><TableHead>Owner</TableHead>
                <TableHead>Follow-up</TableHead><TableHead>Stage</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((l) => (
                  <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell>{l.source}</TableCell>
                    <TableCell>{l.product_interest}</TableCell>
                    <TableCell className="text-xs">{l.owner_name}</TableCell>
                    <TableCell className="font-num text-xs">{l.next_follow_up || "—"}</TableCell>
                    <TableCell>
                      {canEdit && l.stage !== "converted" ? (
                        <Select value={l.stage} onValueChange={(v) => setStage(l.id, v)}>
                          <SelectTrigger className="h-8 w-36" data-testid={`lead-stage-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.filter((s) => s !== "converted").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <StatusBadge status={l.stage} />}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canEdit && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => addActivity(l.id)} title="Log activity"><Activity className="h-4 w-4" /></Button>
                          {l.stage !== "converted" && <Button size="sm" variant="outline" className="gap-1" onClick={() => convert(l.id)} data-testid={`convert-lead-${l.id}`}><UserCheck className="h-4 w-4" /> Convert</Button>}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
